import { useMemo, useRef, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColDef, ICellRendererParams } from 'ag-grid-community';

import Table from '~/shared/components/Table';
import { useXFetch } from '~/shared/utils/useXFetch';
import {
  OptLogVolumeActionsCountResponse,
  OptLogVolumeActionsResponse,
  OptLogVolumeActions,
} from '~/shared/types/api';
import { TABLE_LIVE_REFRESH_MS } from '~/shared/constants/polling';
import { formatDate } from '~/shared/utils/formatNumber';
import OptimizationActions from '~/reports/components/ReportsPage/components/OptimizationActions';
import TypeCell from '~/reports/components/ReportTable/TypeCell';
import { getActionByColumnDef } from '~/reports/components/ReportTable/columnDefs';
import SizeCell from '~/shared/components/SizeCell';
import { generateSkeletonRows, createSkeletonAction } from '~/shared/utils/skeletonUtils';
import { SkeletonCellRenderer } from '~/shared/components/Table/SkeletonCellRenderer';
import {
  ActionTypeFilterValue,
  appendActionTypeToUrl,
} from '~/shared/components/filters/actionTypeFilter';
import { useActionTypeFilterCounts } from '~/shared/hooks/useActionTypeFilterCounts';
import { useAllActionsCount } from '~/shared/hooks/useAllActionsCount';
import { usePermission } from '~/shared/hooks/usePermission';
import { useOptimizationActionsRowData } from '~/shared/hooks/useOptimizationActionsRowData';

interface VolumeActionsLogProps {
  tableQueryUrl: string;
  countQueryUrl: string;
  countBaseQueryString: string;
  onPageChange: (page: number) => void;
  onSortChange: (sort: { field: string; dir: 'desc' | 'asc' }) => void;
  dataLimit: number;
  currentSortField: { field: string; dir: 'desc' | 'asc' };
  currentOffset: string;
  timeSignature: string;
  filtersSignature: string;
}

interface VolumeActionsQueryResult {
  actions: OptLogVolumeActions[];
  count: number;
}

export default function VolumeActionsLog({
  tableQueryUrl,
  countQueryUrl,
  countBaseQueryString,
  onPageChange,
  onSortChange,
  dataLimit,
  currentSortField,
  currentOffset,
  timeSignature,
  filtersSignature,
}: VolumeActionsLogProps) {
  const api = useXFetch();

  const [selectedActionType, setSelectedActionType] = useState<ActionTypeFilterValue>('');

  const handleActionTypeChange = useCallback((type: ActionTypeFilterValue) => {
    setSelectedActionType(type);
    onPageChange(0);
  }, [onPageChange]);

  const activeTableQueryUrl = useMemo(
    () => appendActionTypeToUrl(tableQueryUrl, selectedActionType),
    [tableQueryUrl, selectedActionType]
  );

  const activeCountQueryUrl = useMemo(
    () => appendActionTypeToUrl(countQueryUrl, selectedActionType),
    [countQueryUrl, selectedActionType]
  );

  const { typeCounts, isLoading: typeCountsLoading } = useActionTypeFilterCounts({
    baseCountQueryString: countBaseQueryString,
  });

  const { allActionsCount, allActionsCountLoading } = useAllActionsCount({
    baseCountQueryString: countBaseQueryString,
  });
  const canReadUsers = usePermission('datafy.read.users');

  const { data, isFetching, isPlaceholderData, isLoading } = useQuery<VolumeActionsQueryResult>({
    queryKey: [
      'getOptimizationActions',
      activeTableQueryUrl,
      activeCountQueryUrl,
      currentOffset,
      timeSignature,
      filtersSignature,
    ],
    refetchOnWindowFocus: false,
    enabled: !!tableQueryUrl && !!countQueryUrl,
    refetchInterval: TABLE_LIVE_REFRESH_MS,
    queryFn: async () => {
      const actionsResponse = await api<OptLogVolumeActionsResponse>(activeTableQueryUrl);
      const countResponse = await api<OptLogVolumeActionsCountResponse>(activeCountQueryUrl);
      const actions = actionsResponse.actions ?? [];
      const count = countResponse.count ?? 0;
      return { actions, count };
    },
  });

  const columnDefs = useMemo(
    () => getVolumeActionsColumnDefs({ sortField: currentSortField }),
    [currentSortField]
  );

  const lastKnownCountRef = useRef<number | undefined>(undefined);
  if ((data as unknown as VolumeActionsQueryResult)?.count !== undefined) {
    lastKnownCountRef.current = (data as unknown as VolumeActionsQueryResult).count;
  }
  const stableCount = lastKnownCountRef.current ?? 0;

  const currentPage = useMemo(
    () => Math.floor((parseInt(currentOffset, 10) || 0) / dataLimit),
    [currentOffset, dataLimit]
  );

  const showLoader = (isLoading && !data) || (isFetching && !data && !isPlaceholderData);

  const baseRowData = useMemo(() => {
    if (
      showLoader &&
      (!(data as unknown as VolumeActionsQueryResult)?.actions ||
        (data as unknown as VolumeActionsQueryResult).actions.length === 0)
    ) {
      return generateSkeletonRows(dataLimit, createSkeletonAction());
    }
    return (data as unknown as VolumeActionsQueryResult)?.actions || [];
  }, [(data as unknown as VolumeActionsQueryResult)?.actions, showLoader, dataLimit]);

  const displayRowData = useOptimizationActionsRowData({
    tableQueryUrl,
    baseRowData,
    canReadUsers,
    showLoader,
  });

  return (
    <div className="flex flex-col gap-[14px] mt-4">
      <div>
        <OptimizationActions
          selectedActionType={selectedActionType}
          onActionTypeChange={handleActionTypeChange}
          typeCounts={typeCounts}
          typeCountsLoading={typeCountsLoading}
          allActionsCount={allActionsCount}
          allActionsCountLoading={allActionsCountLoading}
        />
      </div>

      <div className="flex flex-col pb-[14px] h-[520px] 2xl:h-[702px] ">
        <Table<OptLogVolumeActions>
          columnDefs={columnDefs}
          rowData={displayRowData}
          onSortChange={(sort) => {
            // Prevent sorting when loading skeleton data
            if (
              showLoader &&
              (!(data as unknown as VolumeActionsQueryResult)?.actions ||
                (data as unknown as VolumeActionsQueryResult).actions.length === 0)
            ) {
              return;
            }
            onSortChange(sort);
          }}
          pagination={{
            currentPage: currentPage,
            totalPages: Math.ceil(stableCount / dataLimit),
            dataCount: showLoader ? 0 : stableCount,
            perPage: dataLimit,
            onChange: (page: number) => onPageChange(page),
          }}
          loading={false}
          suppressNoRowsWhileLoading={true}
          itemsName="actions"
          getRowId={(params) => {
            if ((params.data as OptLogVolumeActions & { _isSkeleton?: boolean })._isSkeleton) {
              return params.data.volumeId;
            }
            return `${params.data.volumeId}-${params.data.actionFinishTime}`;
          }}
        />
      </div>
    </div>
  );
}

const getVolumeActionsColumnDefs = ({
  sortField,
}: {
  sortField: { field: string; dir: 'desc' | 'asc' };
}): ColDef<OptLogVolumeActions & { _isSkeleton?: boolean }>[] => [
  {
    headerName: 'Type',
    flex: 2,
    minWidth: 200,
    maxWidth: 278,
    field: 'type',
    cellRenderer: (
      params: ICellRendererParams<OptLogVolumeActions & { _isSkeleton?: boolean }>
    ) => {
      const { data, node } = params;
      if (data?._isSkeleton) {
        return <SkeletonCellRenderer width={200} />;
      }
      if (!data?.type) {
        return null;
      }
      const uniqueId = `${data.volumeId || 'vol'}-${data.actionFinishTime || node.rowIndex}`;
      return (
        <TypeCell
          key={uniqueId}
          type={data.type}
          volumeId={uniqueId}
          progress={data.progress}
          state={data.state}
        />
      );
    },
  },
  {
    headerName: 'Original Size',
    flex: 1,
    minWidth: 120,
    field: 'originalDiskSizeGb',
    cellRenderer: ({
      data,
    }: ICellRendererParams<OptLogVolumeActions & { _isSkeleton?: boolean }>) => {
      if (data?._isSkeleton) {
        return <SkeletonCellRenderer width={100} />;
      }
      return data?.originalDiskSizeGb ? (
        <SizeCell size={data.originalDiskSizeGb} sizeInGiB />
      ) : null;
    },
    sort: sortField?.field === 'originalDiskSizeGb' ? sortField?.dir : null,
  },
  {
    headerName: 'New Size',
    flex: 1,
    minWidth: 120,
    field: 'newDiskSizeGb',
    cellRenderer: ({
      data,
    }: ICellRendererParams<OptLogVolumeActions & { _isSkeleton?: boolean }>) => {
      if (data?._isSkeleton) {
        return <SkeletonCellRenderer width={100} />;
      }
      return data?.newDiskSizeGb ? <SizeCell size={data.newDiskSizeGb} sizeInGiB /> : null;
    },
    sort: sortField?.field === 'newDiskSizeGb' ? sortField?.dir : null,
  },
  {
    headerName: 'Start Time',
    flex: 1.2,
    minWidth: 140,
    field: 'actionStartTime',
    cellRenderer: ({
      data,
      value,
    }: ICellRendererParams<OptLogVolumeActions & { _isSkeleton?: boolean }>) => {
      if (data?._isSkeleton) {
        return <SkeletonCellRenderer width={120} />;
      }
      return value ? formatDate(value, true) : null;
    },
    sort: sortField?.field === 'actionStartTime' ? sortField?.dir : null,
    cellStyle: { fontWeight: 500, color: '#071031', fontSize: '14px', lineHeight: '20px' },
  },
  {
    headerName: 'End Time',
    flex: 1.2,
    minWidth: 140,
    field: 'actionFinishTime',
    cellRenderer: ({
      data,
      value,
    }: ICellRendererParams<OptLogVolumeActions & { _isSkeleton?: boolean }>) => {
      if (data?._isSkeleton) {
        return <SkeletonCellRenderer width={120} />;
      }
      return data?.state && ['Success', 'Failure', 'Canceled'].includes(data.state)
        ? formatDate(value, true)
        : null;
    },
    sort: sortField?.field === 'actionFinishTime' ? sortField?.dir : null,
    cellStyle: { fontWeight: 500, color: '#071031', fontSize: '14px', lineHeight: '20px' },
  },
  getActionByColumnDef(),
];
